import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import type { CreateNewBlogPostContentPageMutationMutation } from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import { CpsSpinner } from "corpus";

import { BlogPostForm } from "@/components/blog/blog-post-form.tsx";
import { BlogPostFormSchema } from "@/components/blog/type/blog-post-form-schema.tsx";
import {
  HeaderButton,
  HeaderIcon,
  HeaderRoot,
  HeaderTextButton,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const CreateNewBlogPostContentPageMutation = graphql(`
  mutation CreateNewBlogPostContentPageMutation($title: String!, $content: String!) {
    criarOuAlterarPublicacaoBlog(input: { titulo: $title, conteudo: $content }) {
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
      publicacaoBlogGraphQLType {
        idUsuarioPosts
      }
    }
  }
`);

const CreateNewBlogPostContentPage = (): JSX.Element => {
  const router = useRouter();
  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    CreateNewBlogPostContentPageMutation,
  );

  const form = useForm<z.infer<typeof BlogPostFormSchema>>({
    resolver: zodResolver(BlogPostFormSchema),
    values: {
      idUsuarioPosts: null,
      titulo: "",
      conteudo: "",
    },
  });

  const onSubmit = (response: z.infer<typeof BlogPostFormSchema>): void => {
    const onSuccess = (
      responseData: CreateNewBlogPostContentPageMutationMutation,
    ): void => {
      if (
        !responseData.criarOuAlterarPublicacaoBlog.publicacaoBlogGraphQLType
          ?.idUsuarioPosts
      ) {
        return;
      }
      void router.navigate({
        to: "/settings/blog/$userPostId",
        params: {
          userPostId:
            responseData.criarOuAlterarPublicacaoBlog.publicacaoBlogGraphQLType
              .idUsuarioPosts,
        },
        replace: true,
      });
    };
    void mutateAsync(
      { content: response.conteudo, title: response.titulo },
      { onSuccess },
    );
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Criar conteúdo" />
        {isPending ? (
          <HeaderIcon icon={CpsSpinner} align="end" className="h-6" />
        ) : (
          <HeaderTextButton
            align="end"
            text="Salvar"
            onClick={() => {
              void form.handleSubmit(onSubmit)();
            }}
          />
        )}
      </HeaderRoot>
      <BlogPostForm form={form} />
    </>
  );
};

export const Route = createFileRoute("/settings/blog/create")({
  component: CreateNewBlogPostContentPage,
});
